import React, { useEffect, useRef, useState } from 'react';
import { hapticError, hapticSuccess, hapticTick } from '../../../_utils/haptics';
import { cleanCode, dateSaid, headlineOf, looksLikeGiftCard, money2, termsOf, valueSaid } from '../data/codes';
import './client.css';

/**
 * What a client sees of a discount code on the booking page:
 *   CodeBanner  above the services: what's applied, or why it can't be
 *   CodeTag     on each service the code covers (and only those)
 *   CodeBox     "Have a discount code?" in the confirm sheet, for guests
 *               and signed-in clients alike, with or without a deposit
 * Plain words, real text (so it can be read and copied), and the result is
 * announced to screen readers.
 */

const TagIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M20.6 13.4 13.4 20.6a2 2 0 0 1-2.8 0L3 13V3h10l7.6 7.6a2 2 0 0 1 0 2.8z" /><circle cx="7.5" cy="7.5" r="1.2" fill="currentColor" />
  </svg>
);
const CloseIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12" /></svg>
);

// Does this code cover this service on the booking page? (Shown only where
// it applies: ASA, Tefal 2025.) Stylist and consent checks finish at confirm.
export const coversService = (code, service) => {
  if (!code || !service) return false;
  const ch = code.channels || {};
  if (ch.online === false) return false;
  const s = code.scope || {};
  const ids = (service.hairdressers || []).map((h) => String(h && (h._id || h.id)));
  if (code.ownerType === 'stylist' && ids.length && !ids.includes(String(code.ownerId))) return false;
  if (code.ownerType !== 'stylist' && (s.stylistIds || []).length && ids.length && !s.stylistIds.map(String).some((id) => ids.includes(id))) return false;
  if (s.services !== 'some') return true;
  const sid = String(service.commonServiceId || service.id);
  return (s.serviceIds || []).map(String).includes(sid) || (!!service.categoryId && (s.categoryIds || []).map(String).includes(String(service.categoryId)));
};

const usable = (info) => !!(info && info.found && info.code && info.state === 'live');

export function CodeBanner({ code }) {
  const [open, setOpen] = useState(false);
  if (!code || !code.enabled || !code.typed || code.dismissed || !code.info) return null;
  const { info } = code;
  const c = info.code;
  if (usable(info)) {
    return (
      <div className="dcc-banner" role="status">
        <span className="dcc-banner-icon"><TagIcon /></span>
        <div className="dcc-banner-text">
          <b>{c.code} applied</b>
          <span>{headlineOf(c)}.{c.endsOn ? ` Book by ${dateSaid(c.endsOn)}.` : ''} {open ? '' : <button type="button" className="dcc-link" onClick={() => { hapticTick(); setOpen(true); }}>Details</button>}</span>
          {open && <span className="dcc-banner-terms">{termsOf(c, { long: true })}</span>}
        </div>
        <button type="button" className="dcc-x" aria-label={`Remove ${c.code}`} onClick={() => { hapticTick(); code.remove(); }}><CloseIcon /></button>
      </div>
    );
  }
  const typed = code.typed;
  let text;
  if (!info.found) text = `We couldn’t find the code ${typed}. You can still book at the usual prices.`;
  else if (info.state === 'scheduled') text = `${c.code} starts on ${dateSaid(c.startsOn)}. Until then, prices are as usual.`;
  else if (info.state === 'paused') text = `${c.code} isn’t available at the moment. You can still book at the usual prices.`;
  else text = `${c.code} has ended. You can still book at the usual prices.`;
  return (
    <div className="dcc-banner dcc-banner--off" role="status">
      <span className="dcc-banner-icon"><TagIcon /></span>
      <div className="dcc-banner-text"><span>{text}</span></div>
      <button type="button" className="dcc-x" aria-label="Dismiss" onClick={() => { hapticTick(); code.remove(); }}><CloseIcon /></button>
    </div>
  );
}

export function CodeTag({ code, service }) {
  if (!code || !code.enabled || !code.typed || !usable(code.info)) return null;
  const c = code.info.code;
  if (!coversService(c, service)) return null;
  return <span className="dcc-tag"><TagIcon />{valueSaid(c)} with {c.code}</span>;
}

/**
 * In the confirm sheet. `ctx` is { lines, date, client } for the basket on
 * screen; the code is re-checked whenever it changes.
 */
export function CodeBox({ code, ctx, currency = '£', noun = 'booking' }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [said, setSaid] = useState('');
  const input = useRef(null);
  const key = JSON.stringify([ctx && ctx.lines, ctx && ctx.date, ctx && ctx.client && ctx.client.phone]);

  // Re-check the code in play against this basket
  useEffect(() => {
    if (!code || !code.enabled || !code.typed) return;
    code.check(ctx).then((r) => {
      if (r && r.ok) setSaid(`${r.code ? r.code.code : code.typed} applied. You save ${money2(r.amount)}.`);
    });
  }, [key, code && code.typed]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { if (open && input.current) input.current.focus(); }, [open]);

  if (!code || !code.enabled) return null;
  const r = code.result;
  const applied = !!(code.typed && r && r.ok);
  const notNow = !!(code.typed && r && !r.ok && r.reason !== 'error');

  const submit = async (e) => {
    if (e) e.preventDefault();
    const t = cleanCode(text);
    if (!t) { code.setError('Type a code first.'); hapticError(); return; }
    if (looksLikeGiftCard(text)) { code.setError('That’s a gift card code. Add it as a gift card instead.'); hapticError(); return; }
    const res = await code.apply(t, ctx);
    if (res && res.ok) {
      hapticSuccess();
      setText('');
      setOpen(false);
      setSaid(`${t} applied. You save ${money2(res.amount)}.`);
    } else {
      hapticError();
      setSaid('');
    }
  };

  return (
    <div className="dcc-box">
      <span className="dcc-sr" aria-live="polite">{said}</span>
      {applied && (
        <div className="dcc-applied">
          <span className="dcc-applied-icon"><TagIcon /></span>
          <div className="dcc-applied-text">
            <b>{r.code ? r.code.code : code.typed} applied</b>
            <span>{r.code ? termsOf(r.code, { withCode: false, shop: noun === 'order' }) : ''}</span>
          </div>
          <div className="dcc-applied-right">
            <span className="dcc-applied-amount">−{currency}{Number(r.amount).toFixed(2)}</span>
            <button type="button" className="dcc-link" onClick={() => { hapticTick(); setSaid('Code removed.'); code.remove(); }}>Remove</button>
          </div>
        </div>
      )}
      {notNow && (
        <div className="dcc-applied dcc-applied--off" role="alert">
          <span className="dcc-applied-icon"><TagIcon /></span>
          <div className="dcc-applied-text">
            <b>{code.typed} can’t be used on this {noun}</b>
            <span>{r.message}</span>
          </div>
          <div className="dcc-applied-right">
            <button type="button" className="dcc-link" onClick={() => { hapticTick(); code.remove(); }}>Remove</button>
          </div>
        </div>
      )}
      {!code.typed && !open && (
        <button type="button" className="dcc-open" onClick={() => { hapticTick(); setOpen(true); }}>
          <TagIcon />Have a discount code?
        </button>
      )}
      {!code.typed && open && (
        <form className="dcc-form" onSubmit={submit} noValidate>
          <label className="dcc-label" htmlFor="dcc-input">Discount code</label>
          <div className="dcc-row">
            <input
              ref={input}
              id="dcc-input"
              className={`dcc-input${code.error ? ' is-bad' : ''}`}
              value={text}
              autoCapitalize="characters"
              autoComplete="off"
              autoCorrect="off"
              spellCheck={false}
              enterKeyHint="go"
              placeholder="For example, AUTUMN20"
              aria-invalid={!!code.error}
              aria-describedby={code.error ? 'dcc-error' : undefined}
              onChange={(e) => { const v = e.target.value; setText(v.toUpperCase()); if (code.error) code.setError(''); }}
            />
            <button type="submit" className="dcc-apply" disabled={code.checking || !text.trim()}>{code.checking ? 'Checking…' : 'Apply'}</button>
          </div>
          {code.error && <p className="dcc-error" id="dcc-error" role="alert">{code.error}</p>}
          <button type="button" className="dcc-link dcc-cancel" onClick={() => { setOpen(false); setText(''); code.setError(''); }}>Cancel</button>
        </form>
      )}
    </div>
  );
}
